/**
 * Utilitários de Formatação
 */

/**
 * Converte uma data (string ou Date) para um objeto Date.
 * Se a data vier do backend como string e não tiver timezone,
 * assume que é UTC.
 */
export const parseDate = (date) => {
  if (!date) return null;
  if (date instanceof Date) return date;

  if (typeof date === "string") {
    let formattedDate = date.trim();

    // Se for apenas data "YYYY-MM-DD", tratamos como data local (calendário)
    // para evitar que o fuso horário mude o dia (ex: nascimento).
    const dateOnlyMatch = formattedDate.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (dateOnlyMatch) {
      const [, year, month, day] = dateOnlyMatch;
      return new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
    }

    // Se for formato "YYYY-MM-DD HH:mm:ss", converte para ISO "YYYY-MM-DDTHH:mm:ss"
    if (formattedDate.includes(" ") && !formattedDate.includes("T")) {
      formattedDate = formattedDate.replace(" ", "T");
    }

    // Se tem "T" (datetime) e não tem Z nem offset (+00:00 ou -03:00), assume UTC e adiciona Z
    // Isso é para DATETIMES que vêm do backend.
    if (
      formattedDate.includes("T") &&
      !formattedDate.includes("Z") &&
      !formattedDate.match(/[+-]\d{2}:?\d{2}$/)
    ) {
      formattedDate += "Z";
    }

    const dateObj = new Date(formattedDate);
    
    // Verifica se a data é válida
    if (isNaN(dateObj.getTime())) {
      return new Date(date); // Fallback para o construtor padrão
    }
    
    return dateObj;
  }

  return new Date(date);
};

/**
 * Formata valor monetário em Real brasileiro
 */
export const formatCurrency = (value) => {
  if (value === null || value === undefined) return "R$ 0,00";

  const formattedValue = new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: "BRL",
  }).format(value);
  return formattedValue.replace(/\s/g, " ");
};

/**
 * Formata data completa
 */
export const formatDate = (date, options = {}) => {
  if (!date) return "";

  const dateObj = parseDate(date);

  const defaultOptions = {
    weekday: "short",
    day: "2-digit",
    month: "long",
    year: "numeric",
    ...options,
  };

  return new Intl.DateTimeFormat("pt-BR", defaultOptions).format(dateObj);
};

/**
 * Formata data curta (ex: "sáb, 31 de Janeiro")
 * Garante que apenas o mês comece com letra maiúscula.
 */
export const formatShortDate = (date) => {
  if (!date) return "";

  const dateObj = parseDate(date);

  // Formata partes individualmente para ter controle total
  const weekday = new Intl.DateTimeFormat("pt-BR", {
    weekday: "short",
  }).format(dateObj);
  const day = new Intl.DateTimeFormat("pt-BR", { day: "2-digit" }).format(
    dateObj,
  );
  const month = new Intl.DateTimeFormat("pt-BR", { month: "long" }).format(
    dateObj,
  );

  const capitalizedMonth =
    month.charAt(0).toUpperCase() + month.slice(1).toLowerCase();

  // O formato pt-BR de Intl para weekday "short" pode ou não incluir ponto (ex: "sáb" ou "sáb.")
  return `${weekday.toLowerCase()}, ${day} de ${capitalizedMonth}`;
};

/**
 * Formata hora
 */
export const formatTime = (date) => {
  if (!date) return "";

  const dateObj = parseDate(date);

  return new Intl.DateTimeFormat("pt-BR", {
    hour: "2-digit",
    minute: "2-digit",
  }).format(dateObj);
};

/**
 * Formata data e hora
 */
export const formatDateTime = (date) => {
  if (!date) return "";

  return `${formatShortDate(date)} às ${formatTime(date)}`;
};

/**
 * Formata data para dd/mm/yyyy
 * Converte de UTC (do backend) para o timezone local do usuário.
 */
export const formatDateToDayMonthYear = (date) => {
  if (!date) return "";

  const dateObj = parseDate(date);

  return new Intl.DateTimeFormat("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  }).format(dateObj);
};

/**
 * Formata data e hora no formato dd/mm/yyyy às HH:mm
 */
export const formatDateWithTime = (date) => {
  if (!date) return "";
  const dateObj = parseDate(date);
  return `${formatDateToDayMonthYear(dateObj)} às ${formatTime(dateObj)}`;
};

/**
 * Converte string dd/mm/yyyy para Date
 */
export const parseDayMonthYearToDate = (dateStr) => {
  if (!dateStr) return null;
  const parts = dateStr.split("/");
  if (parts.length !== 3) return null;
  const day = parseInt(parts[0], 10);
  const month = parseInt(parts[1], 10) - 1;
  const year = parseInt(parts[2], 10);
  if (isNaN(day) || isNaN(month) || isNaN(year)) return null;
  return new Date(year, month, day);
};

/**
 * Formata data para YYYY-MM-DD (ISO date) sem deslocamento de timezone
 */
export const formatDateToISO = (date) => {
  if (!date) return "";

  const dateObj = parseDate(date);
  const year = dateObj.getFullYear();
  const month = String(dateObj.getMonth() + 1).padStart(2, '0');
  const day = String(dateObj.getDate()).padStart(2, '0');

  return `${year}-${month}-${day}`;
};

/**
 * Formata CPF
 */
export const formatCPF = (cpf) => {
  if (!cpf) return "";

  // Limpa o CPF, mantendo apenas os dígitos
  const cleaned = cpf.replace(/\D/g, "");
  const len = cleaned.length;

  if (len <= 3) return cleaned;
  if (len <= 6) return `${cleaned.slice(0, 3)}.${cleaned.slice(3)}`;
  if (len <= 9) return `${cleaned.slice(0, 3)}.${cleaned.slice(3, 6)}.${cleaned.slice(6)}`;
  return `${cleaned.slice(0, 3)}.${cleaned.slice(3, 6)}.${cleaned.slice(6, 9)}-${cleaned.slice(9, 11)}`;
};

/**
 * Formata telefone
 */
export const formatPhone = (phone) => {
  if (!phone) return "";

  const cleaned = phone.replace(/\D/g, "");

  if (cleaned.length === 11) {
    return cleaned.replace(/(\d{2})(\d{5})(\d{4})/, "($1) $2-$3");
  }

  if (cleaned.length === 10) {
    return cleaned.replace(/(\d{2})(\d{4})(\d{4})/, "($1) $2-$3");
  }

  return phone;
};

/**
 * Formata CEP
 */
export const formatCEP = (cep) => {
  if (!cep) return "";

  const cleaned = cep.replace(/\D/g, "");

  if (cleaned.length <= 5) return cleaned;

  return cleaned.replace(/(\d{5})(\d{0,3})/, "$1-$2");
};

/**
 * Formata número com separador de milhar
 */
export const formatNumber = (number) => {
  if (number === null || number === undefined) return "0";

  return new Intl.NumberFormat("pt-BR").format(number);
};

/**
 * Formata quantidade de ingressos restantes
 */
export const formatTicketsRemaining = (count) => {
  if (count === 0) return "Esgotado";
  if (count === 1) return "Último ingresso!";
  if (count <= 10) return `Últimos ${count} ingressos!`;
  if (count <= 50) return `Restam ${count} ingressos`;
  return `${formatNumber(count)} disponíveis`;
};

/**
 * Trunca texto com reticências
 */
export const truncateText = (text, maxLength = 100) => {
  if (!text || text.length <= maxLength) return text;

  return text.slice(0, maxLength).trim() + "...";
};

/**
 * Capitaliza primeira letra
 */
export const capitalize = (text) => {
  if (!text) return "";

  return text.charAt(0).toUpperCase() + text.slice(1).toLowerCase();
};

/**
 * Capitaliza cada palavra
 */
export const capitalizeWords = (text) => {
  if (!text) return "";

  return text
    .split(" ")
    .map((word) => capitalize(word))
    .join(" ");
};

/**
 * Formata slug para URL
 */
export const slugify = (text) => {
  if (!text) return "";

  return text
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "");
};

/**
 * Formata tempo relativo (ex: "há 2 horas")
 */
export const formatRelativeTime = (date) => {
  if (!date) return "";

  const dateObj = parseDate(date);
  const now = new Date();
  const diffInSeconds = Math.floor((now - dateObj) / 1000);

  if (diffInSeconds < 60) return "agora mesmo";
  if (diffInSeconds < 3600) return `há ${Math.floor(diffInSeconds / 60)} min`;
  if (diffInSeconds < 86400) return `há ${Math.floor(diffInSeconds / 3600)} h`;
  if (diffInSeconds < 604800)
    return `há ${Math.floor(diffInSeconds / 86400)} dias`;

  return formatShortDate(dateObj);
};

export default {
  formatCurrency,
  formatDate,
  formatShortDate,
  formatTime,
  formatDateTime,
  formatDateToDayMonthYear,
  formatDateToISO,
  parseDayMonthYearToDate,
  formatCPF,
  formatPhone,
  formatCEP,
  formatNumber,
  formatTicketsRemaining,
  truncateText,
  capitalize,
  capitalizeWords,
  slugify,
  formatRelativeTime,
};
